import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Eye, EyeOff, ArrowLeft, Check, Store, User, Mail, Phone, MapPin, Lock, AtSign } from "lucide-react";
import "./Register.css";

interface FormData {
  storeName: string;
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  phone: string;
  storeAddress: string;
  city: string;
  state: string;
  zip: string;
  username: string;
  password: string;
  confirmPassword: string;
}

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY"
];

export default function Register() {
  const navigate = useNavigate();
  const { registerStore } = useAuth();
  const [form, setForm] = useState<FormData>({
    storeName: "",
    firstName: "",
    lastName: "",
    email: "",
    confirmEmail: "",
    phone: "",
    storeAddress: "",
    city: "",
    state: "",
    zip: "",
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [submitted, setSubmitted] = useState(false);

  const update = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const validate = (): boolean => {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!form.storeName.trim()) e.storeName = "Store name is required";
    if (!form.firstName.trim()) e.firstName = "First name is required";
    if (!form.lastName.trim()) e.lastName = "Last name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.confirmEmail.trim()) e.confirmEmail = "Please confirm your email";
    else if (form.email.toLowerCase() !== form.confirmEmail.toLowerCase()) e.confirmEmail = "Emails do not match";
    if (!form.phone.trim()) e.phone = "Phone number is required";
    else if (form.phone.replace(/\D/g, "").length < 10) e.phone = "Enter a valid 10-digit phone number";
    if (!form.storeAddress.trim()) e.storeAddress = "Store address is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.state) e.state = "State is required";
    if (!form.zip.trim()) e.zip = "ZIP code is required";
    else if (!/^\d{5}(-\d{4})?$/.test(form.zip)) e.zip = "Enter a valid ZIP code";
    if (!form.username.trim()) e.username = "Username is required";
    else if (form.username.length < 4) e.username = "Username must be at least 4 characters";
    else if (!/^[a-zA-Z0-9_]+$/.test(form.username)) e.username = "Letters, numbers, and underscores only";
    if (!form.password) e.password = "Password is required";
    else if (form.password.length < 8) e.password = "Password must be at least 8 characters";
    if (!form.confirmPassword) e.confirmPassword = "Please confirm your password";
    else if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords do not match";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setSubmitError("");
    if (validate()) {
      const result = await registerStore({
        storeName: form.storeName,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        storeAddress: form.storeAddress,
        city: form.city,
        state: form.state,
        zip: form.zip,
        username: form.username,
        password: form.password,
      });
      if (result.success) {
        setSubmitted(true);
      } else {
        setSubmitError(result.error || "Registration failed. Please try again.");
      }
    }
  };

  if (submitted) {
    return (
      <div className="register-page">
        <div className="register-card register-success-card">
          <div className="register-success-icon">
            <Check size={40} />
          </div>
          <h2>You're All Set!</h2>
          <p className="register-success-text">
            Your 7-day free trial for <strong>{form.storeName}</strong> is ready.
            We've sent a confirmation email to <strong>{form.email}</strong>.
          </p>
          <div className="register-success-details">
            <div className="register-success-row">
              <span>Username</span>
              <strong>{form.username}</strong>
            </div>
            <div className="register-success-row">
              <span>Store</span>
              <strong>{form.storeName}</strong>
            </div>
            <div className="register-success-row">
              <span>Trial</span>
              <strong>7 days free</strong>
            </div>
          </div>
          <button className="register-submit" onClick={() => navigate("/login")}>
            Go to Login
          </button>
          <p className="register-success-note">Check your inbox to confirm your email address.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="register-page">
      <div className="register-card">
        <button className="register-back" onClick={() => navigate("/")}>
          <ArrowLeft size={18} /> Back
        </button>

        <div className="register-header">
          <div className="register-logo">
            <Sparkles size={28} />
          </div>
          <h1>Start Your 7-Day Free Trial</h1>
          <p>Set up your store in under 2 minutes. No credit card required.</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="register-section">
            <div className="register-section-label">
              <Store size={16} /> Store Information
            </div>
            <div className="register-field">
              <label>Store Name</label>
              <input
                type="text"
                placeholder="e.g. Ashley Furniture - Downtown"
                value={form.storeName}
                onChange={(e) => update("storeName", e.target.value)}
                className={errors.storeName ? "field-error" : ""}
              />
              {errors.storeName && <span className="register-error">{errors.storeName}</span>}
            </div>
            <div className="register-row">
              <div className="register-field">
                <label>Store Address</label>
                <input
                  type="text"
                  placeholder="123 Main St"
                  value={form.storeAddress}
                  onChange={(e) => update("storeAddress", e.target.value)}
                  className={errors.storeAddress ? "field-error" : ""}
                />
                {errors.storeAddress && <span className="register-error">{errors.storeAddress}</span>}
              </div>
            </div>
            <div className="register-row register-row-3">
              <div className="register-field">
                <label>City</label>
                <input
                  type="text"
                  placeholder="City"
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                  className={errors.city ? "field-error" : ""}
                />
                {errors.city && <span className="register-error">{errors.city}</span>}
              </div>
              <div className="register-field">
                <label>State</label>
                <select
                  value={form.state}
                  onChange={(e) => update("state", e.target.value)}
                  className={errors.state ? "field-error" : ""}
                >
                  <option value="">Select</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {errors.state && <span className="register-error">{errors.state}</span>}
              </div>
              <div className="register-field">
                <label>ZIP Code</label>
                <input
                  type="text"
                  placeholder="12345"
                  value={form.zip}
                  onChange={(e) => update("zip", e.target.value.replace(/[^\d-]/g, "").slice(0, 10))}
                  className={errors.zip ? "field-error" : ""}
                />
                {errors.zip && <span className="register-error">{errors.zip}</span>}
              </div>
            </div>
          </div>

          <div className="register-section">
            <div className="register-section-label">
              <User size={16} /> Owner / Manager Info
            </div>
            <div className="register-row">
              <div className="register-field">
                <label>First Name</label>
                <input
                  type="text"
                  placeholder="First name"
                  value={form.firstName}
                  onChange={(e) => update("firstName", e.target.value)}
                  className={errors.firstName ? "field-error" : ""}
                />
                {errors.firstName && <span className="register-error">{errors.firstName}</span>}
              </div>
              <div className="register-field">
                <label>Last Name</label>
                <input
                  type="text"
                  placeholder="Last name"
                  value={form.lastName}
                  onChange={(e) => update("lastName", e.target.value)}
                  className={errors.lastName ? "field-error" : ""}
                />
                {errors.lastName && <span className="register-error">{errors.lastName}</span>}
              </div>
            </div>
            <div className="register-field">
              <label><Mail size={14} /> Email Address</label>
              <input
                type="email"
                placeholder="you@yourstore.com"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className={errors.email ? "field-error" : ""}
              />
              {errors.email && <span className="register-error">{errors.email}</span>}
            </div>
            <div className="register-field">
              <label><Mail size={14} /> Confirm Email</label>
              <input
                type="email"
                placeholder="Confirm your email address"
                value={form.confirmEmail}
                onChange={(e) => update("confirmEmail", e.target.value)}
                className={errors.confirmEmail ? "field-error" : ""}
              />
              {errors.confirmEmail && <span className="register-error">{errors.confirmEmail}</span>}
            </div>
            <div className="register-field">
              <label><Phone size={14} /> Phone Number</label>
              <input
                type="tel"
                placeholder="(555) 123-4567"
                value={form.phone}
                onChange={(e) => update("phone", formatPhone(e.target.value))}
                className={errors.phone ? "field-error" : ""}
              />
              {errors.phone && <span className="register-error">{errors.phone}</span>}
            </div>
          </div>

          <div className="register-section">
            <div className="register-section-label">
              <Lock size={16} /> Account Credentials
            </div>
            <div className="register-field">
              <label><AtSign size={14} /> Username</label>
              <input
                type="text"
                placeholder="Choose a username"
                value={form.username}
                onChange={(e) => update("username", e.target.value.replace(/\s/g, ""))}
                className={errors.username ? "field-error" : ""}
              />
              {errors.username && <span className="register-error">{errors.username}</span>}
            </div>
            <div className="register-field">
              <label><Lock size={14} /> Password</label>
              <div className="register-password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password (8+ characters)"
                  value={form.password}
                  onChange={(e) => update("password", e.target.value)}
                  className={errors.password ? "field-error" : ""}
                />
                <button
                  type="button"
                  className="register-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <span className="register-error">{errors.password}</span>}
              {form.password && !errors.password && (
                <div className="password-strength">
                  <div className={`strength-bar ${form.password.length >= 12 ? "strong" : form.password.length >= 8 ? "medium" : "weak"}`} />
                  <span>{form.password.length >= 12 ? "Strong" : form.password.length >= 8 ? "Good" : "Too short"}</span>
                </div>
              )}
            </div>
            <div className="register-field">
              <label><Lock size={14} /> Confirm Password</label>
              <div className="register-password-field">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={form.confirmPassword}
                  onChange={(e) => update("confirmPassword", e.target.value)}
                  className={errors.confirmPassword ? "field-error" : ""}
                />
                <button
                  type="button"
                  className="register-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.confirmPassword && <span className="register-error">{errors.confirmPassword}</span>}
            </div>
          </div>

          {submitError && <div className="register-submit-error">{submitError}</div>}

          <button type="submit" className="register-submit">
            Create Account & Start Free Trial
          </button>

          <p className="register-terms">
            By creating an account, you agree to our Terms of Service and Privacy Policy.
          </p>

          <div className="register-login-link">
            Already have an account?{" "}
            <a onClick={() => navigate("/login")}>Log in here</a>
          </div>
        </form>
      </div>
    </div>
  );
}
